import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import MasterLayout from '../../masterLayout/MasterLayout'
import Breadcrumb from '../../components/Breadcrumb'
import "./PublishedProduct.css"
import { Icon } from '@iconify/react/dist/iconify.js'
import { Link } from 'react-router-dom'
import { getProductsApi, getCategoryApi, deleteProductApi } from '../../helper/apiHelper'
import { ImageUrl } from '../../helper/apiHelper'
import toast from 'react-hot-toast'
import { setBestSellingProductApi } from '../../helper/apiHelper'

const ProductManagement = () => {

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('dateAdded');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const productsPerPage = 10;
    const navigate = useNavigate();

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Fetch products when category filter changes
    useEffect(() => {
        fetchProducts();
    }, [filterCategory]);

    const fetchCategories = async () => {
        try {
            const response = await getCategoryApi();
            if (response?.data) {
                setCategories(response.data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const categoryId = filterCategory === 'all' ? '' : filterCategory;
            const response = await getProductsApi({ categoryId });
            if (response?.data) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let filtered = [...products];

        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.category?.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Category filtering is now handled by API
        // No need to filter by category here since we fetch products by category

        if (filterStatus === 'inStock') {
            filtered = filtered.filter(product => product.stock > 0);
        } else if (filterStatus === 'outOfStock') {
            filtered = filtered.filter(product => product.stock === 0);
        } else if (filterStatus === 'featured') {
            filtered = filtered.filter(product => product.featured);
        }

        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.productName.localeCompare(b.productName);
                case 'price':
                    return a.currentPrice - b.currentPrice;
                case 'dateAdded':
                default:
                    return new Date(b.createdAt) - new Date(a.createdAt);
            }
        });

        setFilteredProducts(filtered);
        setCurrentPage(1);
    }, [searchTerm, filterStatus, sortBy, products]);

    // Pagination
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    const handleSelectProduct = (productId) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        } else {
            setSelectedProducts([...selectedProducts, productId]);
        }
    };



    const handleEdit = (productId) => {
        // Navigate to create-product with product id and editing flag using query params
        if (!productId) return;
        navigate(`/create-product?productId=${productId}&editing=true`);
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (productToDelete) {
            try {
                await deleteProductApi(productToDelete.id);

                setShowDeleteModal(false);
                setProductToDelete(null);
                toast.success('Product deleted successfully!');
                fetchProducts()
            } catch (error) {
                console.error('Error deleting product:', error);
                toast.error('Failed to delete product. Please try again.');
            }
        }
    };

    const handleBulkDelete = () => {
        if (selectedProducts.length === 0) {
            alert('Please select products to delete');
            return;
        }

        if (window.confirm(`Delete ${selectedProducts.length} selected products?`)) {
            setProducts(products.filter(p => !selectedProducts.includes(p.id)));
            setSelectedProducts([]);
            alert('Selected products deleted successfully!');
        }
    };








    const [selectedBestSellers, setSelectedBestSellers] = useState([]);
    const [initialBestSellers, setInitialBestSellers] = useState([]);

    // Sync selectedBestSellers with fetched products
    useEffect(() => {
        if (products.length > 0) {
            const bestSellers = products.filter(p => p.isBestSelling).map(p => p.id);
            setSelectedBestSellers(bestSellers);
            setInitialBestSellers(bestSellers);
        }
    }, [products]);

    const handleBestSellerCheckbox = (productId) => {
        if (selectedBestSellers.includes(productId)) {
            setSelectedBestSellers(selectedBestSellers.filter(id => id !== productId));
        } else {
            setSelectedBestSellers([...selectedBestSellers, productId]);
        }
    };

    const hasBestSellerChanges = () => {
        if (selectedBestSellers.length !== initialBestSellers.length) return true;
        const sortedSelected = [...selectedBestSellers].sort();
        const sortedInitial = [...initialBestSellers].sort();
        return JSON.stringify(sortedSelected) !== JSON.stringify(sortedInitial);
    };

    const handleUpdateBestSellers = async () => {

        if (!selectedBestSellers || selectedBestSellers.length === 0) {
            toast.error("Please select at least one product", {
                id: "no-best-seller-selected",
            });
            return;
        }
        try {
            await setBestSellingProductApi({ productIds: selectedBestSellers });
            toast.success('Best seller status updated successfully');
            fetchProducts();
        } catch (error) {
            console.error('Error updating best seller status:', error);
            toast.error(error || 'Failed to update best seller status');
        }
    };

    return (
        <MasterLayout>

            <Breadcrumb title="Product Management" />
            <div className="published-products-container card">
                <div className="container-fluid py-4">

                    <div className="d-flex justify-content-between align-items-center my-8" style={{ flexWrap: "wrap" }}>
                        <div>
                            <h2 className="page-title ">
                                <i className="bi bi-box-seam me-2"></i>
                                Published Products
                            </h2>

                        </div>
                        <div className="d-flex gap-2">
                            {hasBestSellerChanges() && (
                                <button
                                    className="btn btn-warning text-white"
                                    onClick={handleUpdateBestSellers}
                                    disabled={loading}
                                >
                                    <i className="bi bi-star me-2"></i>
                                    Update Best Selling
                                </button>
                            )}

                            <Link to="/create-product">
                                <button className="btn btn-primary">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add New Product
                                </button></Link>
                        </div>
                    </div>

                    <div className="card mb-8">
                        <div className="card-body" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                            {/* Search */}
                            <div className="col-md-4">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Icon icon="mdi:magnify" className="menu-icon" />

                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search products..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-2">
                                <select
                                    className="form-select"
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    <option value="all">All Categories</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.categoryName}
                                        </option>
                                    ))}
                                </select>
                            </div>



                        </div>
                    </div>
                    {selectedProducts.length > 0 && (
                        <div className="mt-3 pt-3 border-top">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted">
                                    {selectedProducts.length} product(s) selected
                                </span>
                                <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>
                                    <i className="bi bi-trash me-1"></i>Delete Selected
                                </button>

                            </div>
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <p className="text-muted mb-0">
                            Showing {indexOfFirstProduct + 1}-{Math.min(indexOfLastProduct, filteredProducts.length)} of {filteredProducts.length} products
                        </p>
                    </div>



                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading products...</p>
                        </div>
                    ) : (
                        <div className="row">
                            {currentProducts.map(product => (
                                <div key={product.id} className="col-lg-3 col-md-4 col-sm-6 mb-6">
                                    <div className="product-card">
                                        {/* Checkbox */}
                                        <div className="product-select d-flex align-items-center gap-2 mb-2">
                                            <input
                                                type="checkbox"
                                                style={{ marginTop: "0" }}
                                                className="form-check-input"
                                                checked={selectedBestSellers.includes(product.id)}
                                                onChange={() => handleBestSellerCheckbox(product.id)}
                                                id={`bestseller-${product.id}`}
                                            />
                                            <label htmlFor={`bestseller-${product.id}`} className="form-check-label fs-14 fw-medium cursor-pointer">
                                                Best Seller
                                            </label>
                                        </div>

                                        <div className="product-badges">
                                            {product.discountPercentage > 0 && (
                                                <span className="badge bg-success">-{product.discountPercentage}%</span>
                                            )}
                                            <button
                                                onClick={() => handleEdit(product?.id)}
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-primary-50 text-primary-600 hover-bg-primary-100"
                                            >
                                                <Icon icon="lucide:edit" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(product)}
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-danger-50 text-danger-600 hover-bg-danger-100"
                                            >
                                                <Icon icon="fluent:delete-24-regular" />
                                            </button>
                                        </div>

                                        <div className="product-image">
                                            <img
                                                src={`${ImageUrl}/${product.mainImage}`}
                                                alt={product.productName}
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                                                }}
                                            />
                                        </div>

                                        <div className="product-info">
                                            <h6 className="product-name">{product.productName}</h6>
                                            <p className="product-category">
                                                {product.category?.categoryName || 'Uncategorized'}
                                            </p>

                                            <div className="product-variants mb-2">
                                                <small className="text-muted">
                                                    {product.variants?.length || 0} variant(s)
                                                </small>
                                            </div>

                                            <div className="product-price">
                                                <span className="current-price">CAD ${product.currentPrice}</span>
                                                {product.discountPercentage > 0 && (
                                                    <span className="original-price">CAD ${product.originalPrice}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}


                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <nav>
                                <ul className="pagination">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="bi bi-chevron-left"></i>
                                        </button>
                                    </li>

                                    {[...Array(totalPages)].map((_, index) => (
                                        <li
                                            key={index}
                                            className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(index + 1)}
                                            >
                                                {index + 1}
                                            </button>
                                        </li>
                                    ))}

                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="bi bi-chevron-right"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}

                    {/* Empty State */}
                    {(filteredProducts.length === 0 && !loading) && (
                        <div className="card">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-inbox display-1 text-muted"></i>
                                <h4 className="mt-3">No Products Found</h4>
                                <p className="text-muted">
                                    {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
                                        ? 'Try adjusting your filters'
                                        : 'Start by adding your first product'}
                                </p>
                                <Link to="/create-product">
                                    <button className="btn btn-primary mt-3">
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add New Product
                                    </button></Link>
                            </div>
                        </div>
                    )}

                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowDeleteModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {productToDelete && (
                                        <div>
                                            <p>Are you sure you want to delete this product?</p>
                                            {/* <div className="alert alert-warning">
                                                <strong>{productToDelete.name}</strong>
                                                <br />
                                            <small>SKU: {productToDelete.sku}</small> 
                                            </div> */}

                                            <p className="text-danger mb-0">
                                                <i className="bi bi-info-circle me-2"></i>
                                                This action cannot be undone.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDeleteConfirm}
                                    >
                                        <i className="bi bi-trash me-2"></i>
                                        Delete Product
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </MasterLayout>
    )
}

export default ProductManagement
